import { z } from "zod";

const englishSlug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const skuPattern = /^[A-Za-z0-9._-]+$/;
const couponPattern = /^[A-Z0-9_-]+$/;
const fullUrl = /^https?:\/\//i;

export const seoSchema = z.object({
  title: z.string().max(200, "عنوان سئو حداکثر ۲۰۰ نویسه می‌تواند باشد.").nullable().optional(),
  description: z.string().max(500, "توضیحات متا حداکثر ۵۰۰ نویسه می‌تواند باشد.").nullable().optional(),
  canonicalUrl: z.string().nullable().optional().refine((value) => !value || fullUrl.test(value), "آدرس Canonical باید URL کامل با http یا https باشد."),
  imageUrl: z.string().nullable().optional(),
  index: z.boolean(),
  follow: z.boolean(),
});

export const loginSchema = z.object({
  identifier: z.string().trim().min(3, "شماره موبایل یا ایمیل حساب مدیریت را وارد کنید."),
  password: z.string().min(1, "رمز عبور را وارد کنید."),
  twoFactorCode: z.string().trim().optional(),
});

export const brandSchema = z.object({
  slug: z.string().trim().min(1, "اسلاگ برند الزامی است.").regex(englishSlug, "اسلاگ فقط با حروف انگلیسی کوچک، عدد و خط تیره وارد شود."),
  name: z.string().trim().min(2, "نام برند باید حداقل ۲ نویسه باشد.").max(120, "نام برند بیش از حد طولانی است."),
  country: z.string().trim().max(80, "نام کشور بیش از حد طولانی است."),
  description: z.string().trim().max(3000, "معرفی برند حداکثر ۳۰۰۰ نویسه می‌تواند باشد."),
  imageUrl: z.string().nullable().optional(),
  isFeatured: z.boolean(),
  isActive: z.boolean(),
  seo: seoSchema,
});

export const categorySchema = z.object({
  slug: z.string().trim().min(1, "اسلاگ دسته‌بندی الزامی است.").regex(englishSlug, "اسلاگ فقط با حروف انگلیسی کوچک، عدد و خط تیره وارد شود."),
  name: z.string().trim().min(2, "نام دسته‌بندی باید حداقل ۲ نویسه باشد.").max(120, "نام دسته‌بندی بیش از حد طولانی است."),
  description: z.string().max(2500, "توضیحات دسته‌بندی حداکثر ۲۵۰۰ نویسه می‌تواند باشد.").nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  displayOrder: z.number().int("ترتیب نمایش باید عدد صحیح باشد.").min(0, "ترتیب نمایش نمی‌تواند منفی باشد."),
  parentId: z.string().nullable().optional(),
  isActive: z.boolean(),
  seo: seoSchema,
});

export const couponSchema = z.object({
  code: z.string().trim().min(3, "کد تخفیف حداقل ۳ نویسه باشد.").max(40, "کد تخفیف حداکثر ۴۰ نویسه باشد.").regex(couponPattern, "کد تخفیف فقط با حروف بزرگ انگلیسی، عدد، خط تیره و زیرخط وارد شود."),
  description: z.string().max(500, "توضیحات کد تخفیف حداکثر ۵۰۰ نویسه باشد.").nullable().optional(),
  discountType: z.enum(["Percentage", "FixedAmount"]),
  amount: z.number().positive("مقدار تخفیف باید بیشتر از صفر باشد."),
  maximumDiscountAmount: z.number().positive("حداکثر تخفیف باید بیشتر از صفر باشد.").nullable().optional(),
  minimumOrderAmount: z.number().min(0, "حداقل مبلغ سفارش نمی‌تواند منفی باشد."),
  startsAt: z.string().min(1, "زمان شروع را مشخص کنید."),
  endsAt: z.string().min(1, "زمان پایان را مشخص کنید."),
  usageLimit: z.number().int("سقف استفاده باید عدد صحیح باشد.").positive("سقف استفاده باید بیشتر از صفر باشد.").nullable().optional(),
  perUserLimit: z.number().int("سقف هر کاربر باید عدد صحیح باشد.").positive("سقف هر کاربر باید بیشتر از صفر باشد.").nullable().optional(),
  isActive: z.boolean(),
}).superRefine((value, ctx) => {
  if (value.discountType === "Percentage" && value.amount > 100) {
    ctx.addIssue({ code: "custom", path: ["amount"], message: "در تخفیف درصدی، مقدار نمی‌تواند بیشتر از ۱۰۰ باشد." });
  }
  if (new Date(value.endsAt).getTime() <= new Date(value.startsAt).getTime()) {
    ctx.addIssue({ code: "custom", path: ["endsAt"], message: "زمان پایان باید بعد از زمان شروع باشد." });
  }
});

export const shippingSchema = z.object({
  code: z.string().trim().min(2, "کد روش ارسال الزامی است.").max(40, "کد روش ارسال بیش از حد طولانی است.").regex(/^[A-Za-z0-9_-]+$/, "کد روش ارسال فقط با حروف انگلیسی، عدد، خط تیره و زیرخط وارد شود."),
  name: z.string().trim().min(2, "نام روش ارسال باید حداقل ۲ نویسه باشد."),
  description: z.string().max(800, "توضیحات روش ارسال حداکثر ۸۰۰ نویسه باشد.").nullable().optional(),
  basePrice: z.number().min(0, "هزینه ارسال نمی‌تواند منفی باشد."),
  freeShippingThreshold: z.number().positive("آستانه ارسال رایگان باید بیشتر از صفر باشد.").nullable().optional(),
  estimatedMinDays: z.number().int("حداقل زمان تحویل باید عدد صحیح باشد.").min(0, "حداقل زمان تحویل نمی‌تواند منفی باشد."),
  estimatedMaxDays: z.number().int("حداکثر زمان تحویل باید عدد صحیح باشد.").min(0, "حداکثر زمان تحویل نمی‌تواند منفی باشد."),
  isActive: z.boolean(),
  displayOrder: z.number().int("ترتیب نمایش باید عدد صحیح باشد.").min(0, "ترتیب نمایش نمی‌تواند منفی باشد."),
}).refine((value) => value.estimatedMaxDays >= value.estimatedMinDays, {
  path: ["estimatedMaxDays"],
  message: "حداکثر زمان تحویل باید برابر یا بیشتر از حداقل زمان باشد.",
});

export const faqSchema = z.object({
  category: z.string().trim().min(2, "دسته پرسش را وارد کنید."),
  question: z.string().trim().min(8, "متن پرسش خیلی کوتاه است.").max(300, "متن پرسش حداکثر ۳۰۰ نویسه باشد."),
  answer: z.string().trim().min(10, "پاسخ باید کامل‌تر نوشته شود.").max(5000, "پاسخ حداکثر ۵۰۰۰ نویسه باشد."),
  displayOrder: z.number().int("ترتیب نمایش باید عدد صحیح باشد.").min(0, "ترتیب نمایش نمی‌تواند منفی باشد."),
  isActive: z.boolean(),
});

export const blogSchema = z.object({
  slug: z.string().trim().min(1, "اسلاگ مقاله الزامی است.").regex(englishSlug, "اسلاگ فقط با حروف انگلیسی کوچک، عدد و خط تیره وارد شود."),
  title: z.string().trim().min(5, "عنوان مقاله باید حداقل ۵ نویسه باشد.").max(200, "عنوان مقاله حداکثر ۲۰۰ نویسه باشد."),
  excerpt: z.string().trim().min(20, "خلاصه مقاله باید حداقل ۲۰ نویسه باشد.").max(700, "خلاصه مقاله حداکثر ۷۰۰ نویسه باشد."),
  content: z.string().trim().min(80, "متن کامل مقاله باید حداقل ۸۰ نویسه باشد."),
  category: z.string().trim().min(2, "دسته مقاله را وارد کنید."),
  imageUrl: z.string().nullable().optional(),
  readTimeMinutes: z.number().int("زمان مطالعه باید عدد صحیح باشد.").min(1, "زمان مطالعه حداقل یک دقیقه است.").max(180, "زمان مطالعه واردشده غیرعادی است."),
  publishedAt: z.string().min(1, "زمان انتشار را مشخص کنید."),
  status: z.enum(["Draft", "Published", "Archived"]),
  isFeatured: z.boolean(),
  seo: seoSchema,
});

const variantSchema = z.object({
  id: z.string().optional(),
  sku: z.string().trim().min(1, "SKU این تنوع الزامی است.").regex(skuPattern, "SKU فقط شامل حروف انگلیسی، عدد، نقطه، خط تیره و زیرخط باشد."),
  volume: z.string().trim().min(1, "حجم یا عنوان تنوع الزامی است."),
  price: z.number().positive("قیمت باید بیشتر از صفر باشد."),
  originalPrice: z.number().positive("قیمت قبل از تخفیف باید بیشتر از صفر باشد.").nullable().optional(),
  stock: z.number().int("موجودی باید عدد صحیح باشد.").min(0, "موجودی نمی‌تواند منفی باشد."),
  isDefault: z.boolean(),
  isTester: z.boolean(),
  isDecant: z.boolean(),
  isActive: z.boolean(),
}).refine((value) => value.originalPrice == null || value.originalPrice > value.price, {
  path: ["originalPrice"],
  message: "قیمت قبل از تخفیف باید از قیمت فروش بیشتر باشد.",
});

const imageSchema = z.object({
  url: z.string().trim().min(1, "آدرس یا فایل تصویر را مشخص کنید."),
  alt: z.string().max(250, "متن جایگزین تصویر حداکثر ۲۵۰ نویسه باشد.").nullable().optional(),
  order: z.number().int().min(0),
  isPrimary: z.boolean(),
});

export const productSchema = z.object({
  slug: z.string().trim().min(1, "اسلاگ محصول الزامی است.").regex(englishSlug, "اسلاگ فقط با حروف انگلیسی کوچک، عدد و خط تیره وارد شود."),
  name: z.string().trim().min(2, "نام محصول باید حداقل ۲ نویسه باشد.").max(200, "نام محصول حداکثر ۲۰۰ نویسه باشد."),
  subtitle: z.string().trim().min(3, "زیرعنوان محصول الزامی است.").max(300, "زیرعنوان محصول حداکثر ۳۰۰ نویسه باشد."),
  shortDescription: z.string().max(700, "توضیح کوتاه حداکثر ۷۰۰ نویسه باشد.").nullable().optional(),
  description: z.string().max(30000, "توضیحات کامل بیش از حد طولانی است.").nullable().optional(),
  brandId: z.string().min(1, "برند محصول را انتخاب کنید."),
  displayBrandName: z.string().nullable().optional(),
  gender: z.enum(["Women", "Men", "Unisex"]),
  concentration: z.string().trim().min(2, "غلظت عطر را مشخص کنید."),
  family: z.string().nullable().optional(),
  nature: z.string().nullable().optional(),
  season: z.string().nullable().optional(),
  occasion: z.string().nullable().optional(),
  countryOfOrigin: z.string().nullable().optional(),
  releaseYear: z.number().int("سال عرضه باید عدد صحیح باشد.").min(1900, "سال عرضه معتبر نیست.").max(new Date().getFullYear() + 1, "سال عرضه معتبر نیست.").nullable().optional(),
  badge: z.enum(["New", "Popular", "Niche", "BestSeller", "Special"]),
  status: z.enum(["Draft", "Published", "Archived"]),
  isFeatured: z.boolean(),
  topNotes: z.string().nullable().optional(),
  middleNotes: z.string().nullable().optional(),
  baseNotes: z.string().nullable().optional(),
  categoryIds: z.array(z.string()).min(1, "حداقل یک دسته‌بندی انتخاب کنید."),
  variants: z.array(variantSchema).min(1, "حداقل یک تنوع محصول ثبت کنید."),
  images: z.array(imageSchema),
  seo: seoSchema,
  searchMetadata: z.object({
    gtin: z.string().nullable().optional().refine((value) => !value || /^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(value), "GTIN معتبر باید ۸، ۱۲، ۱۳ یا ۱۴ رقم باشد."),
    mpn: z.string().max(100, "MPN بیش از حد طولانی است.").nullable().optional(),
    googleProductCategory: z.string().max(180, "Google Product Category بیش از حد طولانی است.").nullable().optional(),
  }),
}).superRefine((value, ctx) => {
  const defaults = value.variants.filter((variant) => variant.isActive && variant.isDefault);
  if (defaults.length !== 1) {
    ctx.addIssue({ code: "custom", path: ["variants"], message: "دقیقاً یک تنوع فعال باید به‌عنوان تنوع پیش‌فرض انتخاب شود." });
  }

  const seen = new Map<string, number>();
  value.variants.forEach((variant, index) => {
    const key = variant.sku.trim().toUpperCase();
    if (seen.has(key)) {
      ctx.addIssue({ code: "custom", path: ["variants", index, "sku"], message: "SKU تنوع‌ها نباید تکراری باشد." });
    } else {
      seen.set(key, index);
    }
  });

  if (value.images.length > 0 && value.images.filter((image) => image.isPrimary).length !== 1) {
    ctx.addIssue({ code: "custom", path: ["images"], message: "اگر تصویر ثبت شده، دقیقاً یک تصویر باید اصلی باشد." });
  }
});

export const profileSchema = z.object({
  firstName: z.string().trim().min(2, "نام باید حداقل ۲ نویسه باشد.").max(80, "نام بیش از حد طولانی است."),
  lastName: z.string().trim().min(2, "نام خانوادگی باید حداقل ۲ نویسه باشد.").max(100, "نام خانوادگی بیش از حد طولانی است."),
  email: z.string().trim().email("ایمیل واردشده معتبر نیست.").or(z.literal("")),
  avatarUrl: z.string(),
});

export const passwordSchema = z.object({
  currentPassword: z.string().min(1, "رمز عبور فعلی را وارد کنید."),
  newPassword: z.string()
    .min(8, "رمز عبور جدید باید حداقل ۸ نویسه باشد.")
    .regex(/[A-Z]/, "رمز عبور جدید باید حداقل یک حرف بزرگ انگلیسی داشته باشد.")
    .regex(/[a-z]/, "رمز عبور جدید باید حداقل یک حرف کوچک انگلیسی داشته باشد.")
    .regex(/\d/, "رمز عبور جدید باید حداقل یک عدد داشته باشد.")
    .regex(/[^A-Za-z0-9]/, "رمز عبور جدید باید حداقل یک نویسه ویژه داشته باشد."),
  confirmPassword: z.string(),
}).refine((value) => value.newPassword === value.confirmPassword, {
  path: ["confirmPassword"],
  message: "تکرار رمز عبور با رمز جدید یکسان نیست.",
});


const adminRolesSchema = z.array(z.enum(["Customer", "VIP", "User"])).length(1, "نوع حساب را مشخص کنید.");

export const createUserSchema = z.object({
  firstName: z.string().trim().min(2, "نام باید حداقل ۲ نویسه باشد.").max(80, "نام بیش از حد طولانی است."),
  lastName: z.string().trim().min(2, "نام خانوادگی باید حداقل ۲ نویسه باشد.").max(80, "نام خانوادگی بیش از حد طولانی است."),
  mobile: z.string().trim().regex(/^09\d{9}$/, "شماره موبایل باید با 09 شروع شود و ۱۱ رقم باشد."),
  email: z.string().trim().email("ایمیل معتبر وارد کنید.").or(z.literal("")),
  password: z.string().min(8, "رمز عبور باید حداقل ۸ نویسه باشد.").regex(/[A-Z]/, "رمز عبور حداقل یک حرف بزرگ انگلیسی داشته باشد.").regex(/[a-z]/, "رمز عبور حداقل یک حرف کوچک انگلیسی داشته باشد.").regex(/\d/, "رمز عبور حداقل یک عدد داشته باشد.").regex(/[^A-Za-z0-9]/, "رمز عبور حداقل یک نویسه ویژه داشته باشد."),
  isActive: z.boolean(),
  roles: adminRolesSchema,
});

export const updateUserSchema = createUserSchema.omit({ password: true });
export const adminSetPasswordSchema = z.object({
  newPassword: z.string().min(8, "رمز عبور باید حداقل ۸ نویسه باشد.").regex(/[A-Z]/, "رمز عبور حداقل یک حرف بزرگ انگلیسی داشته باشد.").regex(/[a-z]/, "رمز عبور حداقل یک حرف کوچک انگلیسی داشته باشد.").regex(/\d/, "رمز عبور حداقل یک عدد داشته باشد.").regex(/[^A-Za-z0-9]/, "رمز عبور حداقل یک نویسه ویژه داشته باشد."),
  confirmPassword: z.string(),
}).refine((value) => value.newPassword === value.confirmPassword, { path: ["confirmPassword"], message: "تکرار رمز عبور یکسان نیست." });

export const orderStatusSchema = z.object({
  status: z.enum(["PendingPayment", "Paid", "Processing", "Shipped", "Delivered", "Cancelled", "Refunded", "PaymentReview"]),
  trackingCode: z.string().max(100, "کد رهگیری بیش از حد طولانی است.").optional(),
});

export const contactUpdateSchema = z.object({
  status: z.enum(["New", "InProgress", "Closed"]),
  adminNote: z.string().max(3000, "یادداشت مدیر حداکثر ۳۰۰۰ نویسه باشد.").optional(),
});

export type LoginFormValues = z.infer<typeof loginSchema>;
export type BrandFormValues = z.infer<typeof brandSchema>;
export type CategoryFormValues = z.infer<typeof categorySchema>;
export type CouponFormValues = z.infer<typeof couponSchema>;
export type ShippingFormValues = z.infer<typeof shippingSchema>;
export type FaqFormValues = z.infer<typeof faqSchema>;
export type BlogFormValues = z.infer<typeof blogSchema>;
export type ProductFormValues = z.infer<typeof productSchema>;
export type ProfileFormValues = z.infer<typeof profileSchema>;
export type PasswordFormValues = z.infer<typeof passwordSchema>;
export type CreateUserFormValues = z.infer<typeof createUserSchema>;
export type UpdateUserFormValues = z.infer<typeof updateUserSchema>;
export type AdminSetPasswordFormValues = z.infer<typeof adminSetPasswordSchema>;
export type OrderStatusFormValues = z.infer<typeof orderStatusSchema>;
export type ContactUpdateFormValues = z.infer<typeof contactUpdateSchema>;


const staffRoleSchema = z.enum([
  "Admin",
  "StoreManager",
  "ProductManager",
  "InventoryManager",
  "OrderManager",
  "Finance",
  "Shipping",
  "CustomerSupport",
  "ContentManager",
  "MarketingManager",
  "SeoManager",
  "Analyst",
  "Auditor",
]);

export const staffUserSchema = z.object({
  firstName: z.string().trim().min(2, "نام باید حداقل ۲ نویسه باشد.").max(80, "نام بیش از حد طولانی است."),
  lastName: z.string().trim().min(2, "نام خانوادگی باید حداقل ۲ نویسه باشد.").max(80, "نام خانوادگی بیش از حد طولانی است."),
  mobile: z.string().trim().regex(/^09\d{9}$/, "شماره موبایل باید با 09 شروع شود و ۱۱ رقم باشد."),
  email: z.string().trim().email("ایمیل معتبر وارد کنید.").or(z.literal("")),
  password: z.string().min(8, "رمز عبور باید حداقل ۸ نویسه باشد.").regex(/[A-Z]/, "رمز عبور حداقل یک حرف بزرگ انگلیسی داشته باشد.").regex(/[a-z]/, "رمز عبور حداقل یک حرف کوچک انگلیسی داشته باشد.").regex(/\d/, "رمز عبور حداقل یک عدد داشته باشد.").regex(/[^A-Za-z0-9]/, "رمز عبور حداقل یک نویسه ویژه داشته باشد."),
  isActive: z.boolean(),
  roles: z.array(staffRoleSchema).min(1, "حداقل یک نقش مدیریتی انتخاب کنید."),
});
export const updateStaffUserSchema = staffUserSchema.omit({ password: true });
export type StaffUserFormValues = z.infer<typeof staffUserSchema>;
export type UpdateStaffUserFormValues = z.infer<typeof updateStaffUserSchema>;
